"use client";

import { Activity, Brain, Compass, Flame, ChartLine as LineChart, Radar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ClinicalFeatures() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const capabilities = [{
    number: "01",
    title: "Surgical Precision",
    description: "Advanced surgical instrumentation and robotic-assisted systems sourced from global leaders to ensure optimal patient outcomes.",
    Icon: LineChart
  }, {
    number: "02",
    title: "Diagnostic Accuracy",
    description: "High-fidelity imaging and diagnostic platforms that provide clinicians with the clarity needed for early, accurate intervention.",
    Icon: Brain
  }, {
    number: "03",
    title: "Aesthetic Innovation",
    description: "Premium dermatological and aesthetic technologies designed to deliver safe, effective, and patient-centered cosmetic results.",
    Icon: Radar
  }, {
    number: "04",
    title: "Dental Solutions",
    description: "Comprehensive dental implant and digital workflow systems that streamline practice efficiency and enhance restorative care.",
    Icon: Compass
  }, {
    number: "05",
    title: "AI-Enabled Healthcare",
    description: "Next-generation artificial intelligence tools that integrate seamlessly into clinical workflows to augment diagnostic decision-making.",
    Icon: Activity
  }, {
    number: "06",
    title: "Digital Connectivity",
    description: "Interoperable digital health platforms that bridge the gap between patient data and clinical action for better care coordination.",
    Icon: Flame
  }];
  return <section data-section-id="4553" id="clinicalfeaturesgrid" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <p className="font-default text-xs tracking-widest uppercase text-blue-700 mb-6">
              03 / Our Expertise
            </p>
            <h2 className="font-default font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-gray-900">
              Clinical excellence, delivered globally.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 flex items-end">
            <p className="font-default text-base md:text-lg leading-relaxed text-gray-600 max-w-2xl">
              We bridge the gap between innovative medical manufacturers and healthcare providers. Our curated portfolio of premium devices is selected through rigorous clinical evaluation to ensure safety, reliability, and superior performance in every medical setting.
            </p>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
          {capabilities.map((cap, i) => {
          const Icon = cap.Icon;
          return <div data-index={i} key={cap.number} className="group relative bg-white border border-transparent hover:border-blue-600/20 transition-all duration-500 ease-out p-8 sm:p-10 flex flex-col" style={{
            aspectRatio: "4 / 5",
            minHeight: "420px"
          }}>
                {}
                <span className="font-default text-xs tracking-widest text-blue-600">
                  {cap.number}
                </span>

                {}
                <div className="relative flex items-center justify-start mt-10 mb-10">
                  <div aria-hidden="true" className="absolute -inset-6 rounded-lg bg-blue-50 blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
                  <Icon strokeWidth={1.5} className="relative size-8 text-gray-900 group-hover:text-blue-600 transition-colors duration-500" />
                </div>

                {}
                <h3 className="font-default font-semibold text-2xl leading-snug text-gray-900 mb-4">
                  {cap.title}
                </h3>

                {}
                <p className="font-default text-[15px] leading-relaxed text-gray-600 mb-8">
                  {cap.description}
                </p>

                {}
                <div className="mt-auto">
                  <Link to="/OurProductsPage" className="inline-flex items-center gap-2 font-default text-xs tracking-widest uppercase text-gray-900 hover:text-blue-600 transition-colors duration-300">
                    <span className="relative">
                      <span className="bg-left-bottom bg-gradient-to-r from-blue-600 to-blue-600 bg-[length:0%_1px] bg-no-repeat group-hover:bg-[length:100%_1px] transition-[background-size] duration-500 ease-out pb-0.5">
                        Learn More
                      </span>
                    </span>
                    <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </div>;
        })}
        </div>

        {}
        <div className="mt-16 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-gray-200">
          <p className="font-default text-sm text-gray-600 max-w-xl">
            Partner with MS Biotechnology to bring world-class medical technologies to your market.
          </p>
          <Button asChild size="lg" className="bg-blue-600 text-white hover:bg-blue-700 font-default text-xs tracking-widest uppercase rounded-lg">
            <Link to="/CompliancePage">Contact Our Team</Link>
          </Button>
        </div>
      </div>
    </section>;
}